import { useEffect, useMemo, useState } from "react";
import { cpFetch, getDefaultTenantId, isProductionHost } from "@/lib/cpClient";
import { useAuthStore } from "@/store/authStore";
import PageShell from "@/components/PageShell";
import { KeyRound } from "lucide-react";

type SecretDef = {
  key: string;
  label: string;
  required: boolean;
  validator: string;
  hint?: string | null;
};

type NamespaceDef = {
  id: string;
  label: string;
  description: string;
  fixed_scope?: { env: string; tenant: string } | null;
  runtime_scope?: { env: string; tenant: string; locked?: boolean; reason?: string } | null;
  platform_oauth_client?: string | null;
  secrets: SecretDef[];
};

type PlatformClientPart = {
  status: "set" | "not_set";
  checksum: string | null;
  set_at: string | null;
  set_by: string | null;
};

type PlatformClientStatus = {
  provider_key: string;
  label: string;
  namespace: string;
  env: string;
  tenant: string;
  keys: { client_id: string; client_secret: string };
  status: "set" | "not_set" | "partial";
  client_id: PlatformClientPart;
  client_secret: PlatformClientPart;
};

type CatalogResponse = {
  ok: boolean;
  environments: string[];
  namespaces: NamespaceDef[];
};

type Entry = {
  key: string;
  label: string;
  required: boolean;
  validator: string;
  hint?: string | null;
  status: "set" | "missing";
  meta: null | {
    updatedAt: string | null;
    updatedBy: string | null;
    reason: string | null;
    checksumSha256: string | null;
    valueLength: number | null;
  };
};

type InfraChangeset = {
  changeset_id: string;
};

function withInfraChangeset(path: string, changesetId: string): string {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}infra_changeset_id=${encodeURIComponent(changesetId)}`;
}

export default function SecretsManagerPage() {
  const { selectedTenant } = useAuthStore();
  const [tenantChoice, setTenant] = useState<string>(selectedTenant || getDefaultTenantId() || "dev");
  const [envChoice, setEnv] = useState<string>(() => (isProductionHost() ? "prod" : "dev"));
  const [platformClients, setPlatformClients] = useState<PlatformClientStatus[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [namespaceId, setNamespaceId] = useState<string>("");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  // modal state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [value, setValue] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [actionBusy, setActionBusy] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [toast, setToast] = useState<{ type: "ok" | "error"; msg: string } | null>(null);

  // audit state
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditCursor, setAuditCursor] = useState<string | null>(null);
  const [auditItems, setAuditItems] = useState<any[]>([]);

  // bulk state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkMode, setBulkMode] = useState<"paste" | "bootstrap">("paste");

  // rotate modal state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateValue, setRotateValue] = useState("");
  const [rotateReason, setRotateReason] = useState("");
  const [rotateUnderstood, setRotateUnderstood] = useState(false);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateSteps, setRotateSteps] = useState<{ step: string; status: string; error?: string }[]>([]);
  const [rotateDone, setRotateDone] = useState<{ ok: boolean; rotation_id?: string; version_hash?: string; rollback?: string | boolean; manual_recovery_required?: boolean; error?: string } | null>(null);

  const namespaces = catalog?.namespaces || [];
  const environments = catalog?.environments || ["dev", "staging", "prod"];

  const activeNamespace = useMemo(
    () => namespaces.find((n) => n.id === namespaceId) || null,
    [namespaces, namespaceId]
  );
  // Platform-level namespaces (e.g. the Xero platform client) are pinned to one env + tenant.
  const fixedScope = activeNamespace?.fixed_scope || null;
  const env = fixedScope?.env ?? envChoice;
  const tenant = fixedScope?.tenant ?? tenantChoice;
  const runtimeScope = activeNamespace?.runtime_scope || null;
  const runtimeMismatch = !!(
    runtimeScope &&
    (env !== runtimeScope.env || tenant !== runtimeScope.tenant)
  );

  async function loadPlatformClients() {
    try {
      const r = (await cpFetch("/secrets/platform-oauth-clients")) as any;
      setPlatformClients(Array.isArray(r?.clients) ? r.clients : []);
    } catch {
      setPlatformClients(null);
    }
  }

  useEffect(() => {
    const scope = activeNamespace?.runtime_scope;
    if (scope) {
      setEnv(scope.env);
      setTenant(scope.tenant);
      return;
    }
    setTenant(selectedTenant || getDefaultTenantId() || "dev");
  }, [selectedTenant, activeNamespace]);

  useEffect(() => {
    (async () => {
      try {
        setErr("");
        const data = (await cpFetch("/secrets/catalog")) as CatalogResponse;
        setCatalog(data);
        const linked = new URLSearchParams(window.location.search).get("namespace");
        if (linked && data?.namespaces?.some((n) => n.id === linked)) {
          setNamespaceId(linked);
        } else if (!namespaceId && data?.namespaces?.length) {
          setNamespaceId(data.namespaces[0].id);
        }
      } catch (e: any) {
        setErr(String(e?.message || e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadEntries(nextEnv = env, nextTenant = tenant, nextNs = namespaceId) {
    if (!nextEnv || !nextTenant || !nextNs) return;
    setLoading(true);
    setErr("");
    loadPlatformClients();
    try {
      const r = (await cpFetch(
        `/secrets/entries?env=${encodeURIComponent(nextEnv)}&tenant=${encodeURIComponent(
          nextTenant
        )}&namespace=${encodeURIComponent(nextNs)}`
      )) as any;
      setEntries(Array.isArray(r?.entries) ? r.entries : []);
    } catch (e: any) {
      setErr(String(e?.message || e));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, tenant, namespaceId]);

  function openEdit(key: string) {
    setEditingKey(key);
    setValue("");
    setReason("");
    setToast(null);
  }

  function closeEdit() {
    setEditingKey(null);
    setValue("");
    setReason("");
  }

  function parseEnvLines(text: string): Record<string, string> {
    const out: Record<string, string> = {};
    const lines = (text || "").split(/\r?\n/);
    for (const raw of lines) {
      const line = raw.trim();
      if (!line || line.startsWith("#")) continue;
      const eq = line.indexOf("=");
      if (eq <= 0) continue;
      const k = line.slice(0, eq).trim();
      let v = line.slice(eq + 1).trim();
      // strip surrounding quotes if present
      if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
        v = v.slice(1, -1);
      }
      if (!k) continue;
      out[k] = v;
    }
    return out;
  }

  async function saveSecret() {
    if (!editingKey) return;
    if (!reason.trim()) {
      setToast({ type: "error", msg: "Reason is required." });
      return;
    }
    if (!value) {
      setToast({ type: "error", msg: "Value is required." });
      return;
    }

    setActionBusy(true);
    setToast(null);
    try {
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/entries",
        [{ type: "secret_set", key: editingKey, env, tenant, namespace: namespaceId }],
        reason
      );
      await cpFetch(withInfraChangeset(`/secrets/entries/${encodeURIComponent(editingKey)}`, changesetId), {
        method: "PUT",
        headers: { "x-infra-changeset-id": changesetId },
        body: JSON.stringify({
          env,
          tenant,
          namespace: namespaceId,
          value,
          reason,
        }),
      });
      setToast({ type: "ok", msg: "Saved." });
      closeEdit();
      await loadEntries();
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function deleteSecret(key: string) {
    if (!confirm(`Delete ${key}? This will remove it from Vault.`)) return;
    const r = prompt("Reason for delete (required):");
    if (!r || !r.trim()) return;

    setActionBusy(true);
    setToast(null);
    try {
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/entries",
        [{ type: "secret_delete", key, env, tenant, namespace: namespaceId }],
        r
      );
      await cpFetch(withInfraChangeset(`/secrets/entries/${encodeURIComponent(key)}`, changesetId), {
        method: "DELETE",
        headers: { "x-infra-changeset-id": changesetId },
        body: JSON.stringify({
          env,
          tenant,
          namespace: namespaceId,
          reason: r,
        }),
      });
      setToast({ type: "ok", msg: "Deleted." });
      await loadEntries();
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function openBulk(mode: "paste" | "bootstrap") {
    setBulkMode(mode);
    setBulkOpen(true);
    setBulkText("");
    setBulkReason("");
    setToast(null);
  }

  function closeBulk() {
    setBulkOpen(false);
    setBulkText("");
    setBulkReason("");
  }

  async function runBootstrapFromEnv() {
    if (!bulkReason.trim()) {
      setToast({ type: "error", msg: "Reason is required." });
      return;
    }
    setActionBusy(true);
    setToast(null);
    try {
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/bootstrap",
        [{ type: "secrets_bootstrap_from_env", env, tenant, namespace: namespaceId }],
        bulkReason
      );
      const resp = await cpFetch(withInfraChangeset("/secrets/bootstrap-from-env", changesetId), {
        method: "POST",
        headers: { "x-infra-changeset-id": changesetId },
        body: JSON.stringify({
          env,
          tenant,
          namespace: namespaceId,
          overwrite: false,
          reason: bulkReason,
        }),
      });
      const counts = (resp as any)?.results?.reduce?.((acc: any, r: any) => {
        acc[r.action] = (acc[r.action] || 0) + 1;
        return acc;
      }, {}) || null;
      setToast({
        type: "ok",
        msg: counts ? `Bootstrap complete: ${JSON.stringify(counts)}` : "Bootstrap complete.",
      });
      closeBulk();
      await loadEntries();
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function runBulkPasteSet() {
    if (!bulkReason.trim()) {
      setToast({ type: "error", msg: "Reason is required." });
      return;
    }
    const parsed = parseEnvLines(bulkText);
    const keys = Object.keys(parsed);
    if (keys.length === 0) {
      setToast({ type: "error", msg: "No KEY=value lines found." });
      return;
    }

    // Only set keys that exist in the current namespace catalog
    const allowed = new Set((activeNamespace?.secrets || []).map((s) => s.key));
    const toSet = keys.filter((k) => allowed.has(k) && parsed[k] !== "");
    if (toSet.length === 0) {
      setToast({ type: "error", msg: "None of the pasted keys match this namespace." });
      return;
    }

    setActionBusy(true);
    setToast(null);
    try {
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/entries",
        toSet.map((k) => ({ type: "secret_set", key: k, env, tenant, namespace: namespaceId })),
        bulkReason
      );
      let okCount = 0;
      let failCount = 0;
      for (const k of toSet) {
        try {
          await cpFetch(withInfraChangeset(`/secrets/entries/${encodeURIComponent(k)}`, changesetId), {
            method: "PUT",
            headers: { "x-infra-changeset-id": changesetId },
            body: JSON.stringify({
              env,
              tenant,
              namespace: namespaceId,
              value: parsed[k],
              reason: bulkReason,
            }),
          });
          okCount++;
        } catch {
          failCount++;
        }
      }
      setToast({ type: "ok", msg: `Bulk set complete: ok=${okCount} failed=${failCount}` });
      closeBulk();
      await loadEntries();
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function hydrateSecrets() {
    setHydrating(true);
    setToast(null);
    try {
      const res = await cpFetch("/secrets/hydrate", { method: "POST" });
      const data = await res.json();
      if (data.ok) {
        const svcSummary = Object.entries(data.services || {})
          .map(([name, info]: [string, any]) =>
            `${name}: ${info.ok ? "✓" : "✗"}${info.changed ? ` (${info.changed} updated)` : ""}`
          )
          .join(", ");
        setToast({ type: "ok", msg: `Secrets hydrated — ${svcSummary}` });
      } else {
        const errs = Object.entries(data.services || {})
          .filter(([, info]: [string, any]) => !info.ok)
          .map(([name, info]: [string, any]) => `${name}: ${info.error || "failed"}`)
          .join("; ");
        setToast({ type: "error", msg: `Hydration partial — ${errs}` });
      }
    } catch (e: any) {
      setToast({ type: "error", msg: e.message || "Hydration failed" });
    } finally {
      setHydrating(false);
    }
  }

  async function validateAllSet() {
    setActionBusy(true);
    setToast(null);
    try {
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/validate-batch",
        [{ type: "secrets_validate_batch", env, tenant, namespace: namespaceId, onlySet: true }],
        `Validate all set secrets in ${namespaceId}`
      );
      const res = (await cpFetch(withInfraChangeset("/secrets/validate-batch", changesetId), {
        method: "POST",
        headers: { "x-infra-changeset-id": changesetId },
        body: JSON.stringify({
          env,
          tenant,
          namespace: namespaceId,
          onlySet: true,
        }),
      })) as any;
      const rows = Array.isArray(res?.results) ? res.results : [];
      const ok = rows.filter((r: any) => r?.ok === true).length;
      const fail = rows.filter((r: any) => r?.ok === false).length;
      const skipped = rows.filter((r: any) => r?.ok === null).length;
      setToast({ type: fail ? "error" : "ok", msg: `Validate-all: ok=${ok} fail=${fail} skipped=${skipped}` });
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function validateKey(key: string) {
    setActionBusy(true);
    setToast(null);
    try {
      // cpFetch throws on non-2xx which hides useful error details for validation.
      // For validation we want the JSON body even on 400 so we can show a human-friendly reason.
      const normalizedPath = "/cp/secrets/validate";

      // Build headers similar to cpFetch (auth + tenant)
      let token: string | null = null;
      try {
        const auth = localStorage.getItem("dc.auth");
        if (auth) token = JSON.parse(auth)?.token || null;
      } catch {
        token = null;
      }

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (tenant) headers["x-tenant-id"] = tenant;
      const changesetId = await createApprovedInfraChangeSet(
        "cp-secrets/validate",
        [{ type: "secret_validate", key, env, tenant, namespace: namespaceId }],
        `Validate secret ${key} in ${namespaceId}`
      );
      headers["x-infra-changeset-id"] = changesetId;

      const resp = await fetch(withInfraChangeset(normalizedPath, changesetId), {
        method: "POST",
        headers,
        credentials: "omit",
        body: JSON.stringify({ env, tenant, namespace: namespaceId, key }),
      });

      let data: any = null;
      try {
        data = await resp.json();
      } catch {
        data = null;
      }

      if (!resp.ok) {
        const msg =
          data?.error === "missing_secret"
            ? `Missing secret: set ${key} first, then validate.`
            : data?.error
              ? String(data.error)
              : `Validate failed (HTTP ${resp.status})`;
        setToast({ type: "error", msg });
        return;
      }

      setToast({ type: "ok", msg: `Validated (${data?.validator || "ok"})` });
    } catch (e: any) {
      setToast({ type: "error", msg: String(e?.message || e) });
    } finally {
      setActionBusy(false);
    }
  }

  async function createApprovedInfraChangeSet(
    target: string,
    operations: any[],
    reason: string
  ): Promise<string> {
    const title = `Secrets change: ${target}`;
    const description = reason?.trim() || "Secrets operation initiated from Secrets Manager UI";

    const created = (await cpFetch("/infra-changesets", {
      method: "POST",
      body: JSON.stringify({
        title,
        description,
        target,
        operations,
        app_id: namespaceId || null,
      }),
    })) as { success?: boolean; changeset?: InfraChangeset };

    const id = String(created?.changeset?.changeset_id || "");
    if (!id) throw new Error("Failed to create infra changeset");

    await cpFetch(`/infra-changesets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ state: "proposed" }),
    });

    await cpFetch(`/infra-changesets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ state: "approved" }),
    });

    return id;
  }

  async function loadAudit(reset = false) {
    setAuditLoading(true);
    setErr("");
    try {
      const cursor = reset ? null : auditCursor;
      const qs = new URLSearchParams();
      qs.set("env", env);
      qs.set("tenant", tenant);
      qs.set("namespace", namespaceId);
      qs.set("limit", "50");
      if (cursor) qs.set("cursor", cursor);

      const res = (await cpFetch(`/secrets/audit?${qs.toString()}`)) as any;
      const items = Array.isArray(res?.items) ? res.items : [];
      const next = res?.nextCursor || null;

      setAuditItems(reset ? items : [...auditItems, ...items]);
      setAuditCursor(next);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setAuditLoading(false);
    }
  }

  function openRotate() {
    setRotateValue("");
    setRotateReason("");
    setRotateUnderstood(false);
    setRotateBusy(false);
    setRotateSteps([]);
    setRotateDone(null);
    setRotateOpen(true);
  }

  function closeRotate() {
    if (rotateBusy) return; // don't close mid-rotation
    setRotateOpen(false);
  }

  async function runRotation() {
    setRotateBusy(true);
    setRotateSteps([]);
    setRotateDone(null);

    const STEP_LABELS: Record<string, string> = {
      lookup_registry: "Load registry",
      validate_new_value: "Validate key format",
      write_vault: "Write to Vault",
      reload_consumer: "Reload volcano-engine",
      rollback: "Rollback",
      success_path: "Record rotation",
    };

    const updateStep = (step: string, status: string, error?: string) => {
      setRotateSteps((prev) => {
        const existing = prev.findIndex((s) => s.step === step);
        const next = { step, status, ...(error ? { error } : {}) };
        if (existing >= 0) {
          const copy = [...prev];
          copy[existing] = next;
          return copy;
        }
        return [...prev, next];
      });
    };

    // Read auth token from localStorage (same pattern used elsewhere in the page).
    let token: string | null = null;
    try {
      const auth = localStorage.getItem("dc.auth");
      if (auth) token = JSON.parse(auth)?.token || null;
    } catch { token = null; }

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (tenant) headers["x-tenant-id"] = tenant;

    try {
      const response = await fetch("/cp/secrets/rotate", {
        method: "POST",
        headers,
        body: JSON.stringify({
          secret_id: "anthropic_api_key",
          new_value: rotateValue,
          reason: rotateReason,
        }),
        credentials: "omit",
      });

      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => "");
        setRotateDone({ ok: false, error: `HTTP ${response.status}: ${text.slice(0, 200)}` });
        setRotateBusy(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from the buffer.
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const block of events) {
          const lines = block.split("\n");
          let eventName = "";
          let dataPart = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) eventName = line.slice(7).trim();
            if (line.startsWith("data: ")) dataPart = line.slice(6).trim();
          }
          if (!eventName || !dataPart) continue;

          let parsed: any = null;
          try { parsed = JSON.parse(dataPart); } catch { continue; }

          if (eventName === "step") {
            updateStep(parsed.step, parsed.status, parsed.error);
          } else if (eventName === "done") {
            setRotateDone(parsed);
          }
        }
      }
    } catch (e: any) {
      setRotateDone({ ok: false, error: String(e?.message || e) });
    } finally {
      setRotateBusy(false);
    }
  }

  return (
    <PageShell
      title="Secrets"
      subtitle="Write-only secret management. Values are stored in Vault and are never revealed in the UI."
      icon={KeyRound}
      iconColor="bg-red-600"
    >

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white border rounded-lg">
          <div className="text-sm font-medium mb-2">Environment</div>
          <select
            className="w-full border rounded px-3 py-2 disabled:bg-gray-100"
            value={env}
            disabled={!!fixedScope}
            onChange={(e) => setEnv(e.target.value)}
          >
            {environments.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>

        <div className="p-4 bg-white border rounded-lg">
          <div className="text-sm font-medium mb-2">Tenant</div>
          <input
            className="w-full border rounded px-3 py-2 disabled:bg-gray-100"
            value={tenant}
            disabled={!!fixedScope}
            onChange={(e) => setTenant(e.target.value)}
            placeholder="tenant id"
          />
          {fixedScope && (
            <div className="text-xs text-gray-500 mt-2">
              Platform-level namespace: fixed to {fixedScope.env} / {fixedScope.tenant}.
            </div>
          )}
          {runtimeScope && (
            <div className={`text-xs mt-2 ${runtimeMismatch ? "text-amber-800" : "text-gray-500"}`}>
              Runtime reads {runtimeScope.env} / {runtimeScope.tenant}. {runtimeScope.reason}
            </div>
          )}
        </div>

        <div className="p-4 bg-white border rounded-lg">
          <div className="text-sm font-medium mb-2">Namespace</div>
          <select
            className="w-full border rounded px-3 py-2"
            value={namespaceId}
            onChange={(e) => setNamespaceId(e.target.value)}
          >
            {namespaces.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
          {activeNamespace && (
            <div className="text-xs text-gray-500 mt-2">{activeNamespace.description}</div>
          )}
        </div>
      </div>

      {platformClients && platformClients.length > 0 && (
        <div className="p-4 bg-white border rounded-lg space-y-2" data-testid="platform-oauth-clients">
          {platformClients.map((c) => {
            const setPart = c.client_secret.status === "set" ? c.client_secret : c.client_id;
            return (
              <div key={c.provider_key} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <div>
                  <span className="font-medium">{c.label}:</span>{" "}
                  {c.status === "not_set" && <span className="text-orange-700">not set</span>}
                  {c.status === "partial" && (
                    <span className="text-orange-700">
                      partial ({c.client_id.status === "set" ? c.keys.client_secret : c.keys.client_id} not set)
                    </span>
                  )}
                  {c.status === "set" && (
                    <span className="text-green-700">
                      set (checksum {setPart.checksum || "?"}, set at {setPart.set_at || "?"})
                    </span>
                  )}
                </div>
                {namespaceId !== c.namespace && (
                  <button
                    className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                    onClick={() => setNamespaceId(c.namespace)}
                  >
                    Open paste box
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <div
          className={`p-3 rounded border ${
            toast.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-orange-50 border-orange-200 text-orange-800"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {err && (
        <div className="p-3 rounded border bg-red-50 border-red-200 text-red-800">
          {err}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold">Keys</div>
          <div className="flex items-center gap-2">
            <button
              onClick={hydrateSecrets}
              className="px-3 py-1.5 border rounded bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-800 font-medium"
              disabled={hydrating || actionBusy}
              title="Push Vault secrets to running services (Volcano, Agent Creator) without restart"
            >
              {hydrating ? "Hydrating…" : "Hydrate Services"}
            </button>
            <button
              onClick={() => openBulk("bootstrap")}
              className="px-3 py-1.5 border rounded hover:bg-gray-50"
              disabled={actionBusy}
              title="Copy env vars from the server into Vault (skips existing)"
            >
              Bootstrap from env
            </button>
            <button
              onClick={() => openBulk("paste")}
              className="px-3 py-1.5 border rounded hover:bg-gray-50"
              disabled={actionBusy}
              title="Paste KEY=value lines to set multiple secrets (current namespace only)"
            >
              Bulk set
            </button>
            <button
              onClick={() => validateAllSet()}
              className="px-3 py-1.5 border rounded hover:bg-gray-50"
              disabled={actionBusy}
              title="Validate all set keys in this namespace"
            >
              Validate all
            </button>
            <button
              onClick={() => loadEntries()}
              className="px-3 py-1.5 border rounded hover:bg-gray-50"
              disabled={loading}
            >
              Refresh
            </button>
            <button
              onClick={() => {
                const next = !showAudit;
                setShowAudit(next);
                if (next) {
                  setAuditCursor(null);
                  setAuditItems([]);
                  loadAudit(true);
                }
              }}
              className="px-3 py-1.5 border rounded hover:bg-gray-50"
              disabled={auditLoading}
            >
              {showAudit ? "Hide audit" : "Show audit"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-gray-600">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-6 text-gray-600">No keys (or failed to load).</div>
        ) : (
          <div className="divide-y">
            {entries.map((e) => (
              <div key={e.key} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium truncate">{e.key}</div>
                    {e.required && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                        required
                      </span>
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        e.status === "set"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {e.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">{e.label}</div>
                  {e.hint && <div className="text-xs text-gray-500 mt-1">{e.hint}</div>}
                  {runtimeScope && (
                    <div className="text-xs text-gray-500 mt-1">
                      Runtime path devcockpit/{runtimeScope.env}/{runtimeScope.tenant}/{activeNamespace?.id}/{e.key}
                    </div>
                  )}
                  {runtimeMismatch && runtimeScope && (
                    <div className="text-xs text-amber-800 mt-1">
                      This form saves to devcockpit/{env}/{tenant}/{activeNamespace?.id}/{e.key}. The running service will not read it.
                    </div>
                  )}
                  {e.meta?.updatedAt && (
                    <div className="text-xs text-gray-500 mt-1">
                      Updated: {e.meta.updatedAt}
                      {e.meta.updatedBy ? ` by ${e.meta.updatedBy}` : ""}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded hover:bg-gray-50"
                    onClick={() => openEdit(e.key)}
                    disabled={actionBusy}
                  >
                    {e.status === "set" ? "Update" : "Set"}
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded hover:bg-gray-50"
                    onClick={() => validateKey(e.key)}
                    disabled={actionBusy}
                  >
                    Validate
                  </button>
                  <button
                    className="px-3 py-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50"
                    onClick={() => deleteSecret(e.key)}
                    disabled={actionBusy}
                  >
                    Delete
                  </button>
                  {namespaceId === "llm" && e.key === "ANTHROPIC_API_KEY" && (
                    <button
                      className="px-3 py-1.5 border border-purple-400 text-purple-700 rounded hover:bg-purple-50 font-medium"
                      onClick={openRotate}
                      disabled={actionBusy}
                      title="Rotate key in Vault and reload volcano-engine"
                    >
                      Rotate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAudit && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold">Audit trail</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setAuditCursor(null);
                  setAuditItems([]);
                  loadAudit(true);
                }}
                className="px-3 py-1.5 border rounded hover:bg-gray-50"
                disabled={auditLoading}
              >
                Refresh
              </button>
              <button
                onClick={() => loadAudit(false)}
                className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                disabled={auditLoading || !auditCursor}
                title={!auditCursor ? "No more items" : "Load more"}
              >
                Load more
              </button>
            </div>
          </div>

          {auditLoading && auditItems.length === 0 ? (
            <div className="p-6 text-gray-600">Loading…</div>
          ) : auditItems.length === 0 ? (
            <div className="p-6 text-gray-600">No audit items.</div>
          ) : (
            <div className="divide-y">
              {auditItems.map((a, idx) => (
                <div key={a?._id || idx} className="p-4 text-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="font-medium">
                      {a.action}
                      {a.key ? `: ${a.key}` : ""}
                    </div>
                    <div className="text-xs text-gray-500">{a.ts_iso || a.ts}</div>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    env={a.env || env} tenant={a.tenant || tenant} namespace={a.namespace || namespaceId}
                    {a.validator ? ` validator=${a.validator}` : ""}
                    {a.ok === false ? " ok=false" : a.ok === true ? " ok=true" : ""}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    actor={a.actor?.email || "unknown"} roles={Array.isArray(a.actor?.roles) ? a.actor.roles.join(",") : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {editingKey && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg mx-4 rounded-lg border shadow">
            <div className="px-5 py-4 border-b">
              <div className="font-semibold">Set value: {editingKey}</div>
              <div className="text-xs text-gray-500">
                This is write-only. You will not be able to view the value later.
              </div>
            </div>
            <div className="p-5 space-y-4">
              {toast && (
                <div
                  className={`p-3 rounded border ${
                    toast.type === "ok"
                      ? "bg-green-50 border-green-200 text-green-800"
                      : "bg-orange-50 border-orange-200 text-orange-800"
                  }`}
                >
                  {toast.msg}
                </div>
              )}

              <div>
                <div className="text-sm font-medium mb-1">Value</div>
                <input
                  type="password"
                  className="w-full border rounded px-3 py-2"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="paste secret value"
                />
              </div>

              <div>
                <div className="text-sm font-medium mb-1">Reason</div>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="why are you changing this?"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t flex gap-2 justify-end">
              <button className="px-4 py-2 border rounded hover:bg-gray-50" onClick={closeEdit}>
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={saveSecret}
                disabled={actionBusy}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-2xl mx-4 rounded-lg border shadow">
            <div className="px-5 py-4 border-b">
              <div className="font-semibold">
                {bulkMode === "bootstrap" ? "Bootstrap from server environment" : "Bulk set from pasted KEY=value lines"}
              </div>
              <div className="text-xs text-gray-500">
                Write-only. Values are stored in Vault and will not be revealed after save.
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-sm font-medium mb-1">Reason (required)</div>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="why are you changing these?"
                />
              </div>

              {bulkMode === "paste" && (
                <div>
                  <div className="text-sm font-medium mb-1">Paste KEY=value lines</div>
                  <textarea
                    className="w-full border rounded px-3 py-2 font-mono text-xs"
                    rows={10}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={"OPENAI_API_KEY=...\nANTHROPIC_API_KEY=...\n# comments supported"}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Only keys that match this namespace will be applied.
                  </div>
                </div>
              )}

              {bulkMode === "bootstrap" && (
                <div className="text-sm text-gray-700">
                  This will copy any non-empty environment variables from the server process into Vault for this namespace,
                  skipping keys that already exist in Vault.
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t flex gap-2 justify-end">
              <button className="px-4 py-2 border rounded hover:bg-gray-50" onClick={closeBulk}>
                Cancel
              </button>
              {bulkMode === "bootstrap" ? (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  onClick={runBootstrapFromEnv}
                  disabled={actionBusy}
                >
                  Run bootstrap
                </button>
              ) : (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  onClick={runBulkPasteSet}
                  disabled={actionBusy}
                >
                  Apply keys
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Rotate Modal */}
      {rotateOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg mx-4 rounded-lg border shadow">
            <div className="px-5 py-4 border-b">
              <div className="font-semibold text-purple-800">Rotate Anthropic API Key</div>
              <div className="text-xs text-gray-500 mt-1">
                Writes the new key to Vault and reloads volcano-engine immediately.
                Other consumers (orchestrator-api, dam-agent-processor, etc.) still read from{" "}
                <code className="bg-gray-100 px-1 rounded">.env</code> and will not be affected.
              </div>
            </div>

            <div className="p-5 space-y-4">
              {!rotateBusy && rotateSteps.length === 0 && !rotateDone && (
                <>
                  <div className="p-3 rounded border bg-amber-50 border-amber-300 text-amber-800 text-sm">
                    This action writes a new key to Vault across all environments and namespaces, then reloads
                    volcano-engine in place. If the key fails validation, the rotation is automatically rolled back.
                  </div>

                  <div>
                    <div className="text-sm font-medium mb-1">New Anthropic API Key</div>
                    <input
                      type="password"
                      className="w-full border rounded px-3 py-2 font-mono"
                      value={rotateValue}
                      onChange={(ev) => setRotateValue(ev.target.value)}
                      placeholder="sk-ant-..."
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <div className="text-sm font-medium mb-1">Reason</div>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={rotateReason}
                      onChange={(ev) => setRotateReason(ev.target.value)}
                      placeholder="why are you rotating this key?"
                    />
                  </div>

                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rotateUnderstood}
                      onChange={(ev) => setRotateUnderstood(ev.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      I understand this updates Vault and reloads volcano-engine immediately.
                      Rollback is automatic if the health check fails.
                    </span>
                  </label>
                </>
              )}

              {/* Progress timeline */}
              {(rotateBusy || rotateSteps.length > 0) && !rotateDone && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">Rotation in progress…</div>
                  {rotateSteps.map((s) => (
                    <div key={s.step} className="flex items-start gap-2 text-sm">
                      <span className={
                        s.status === "ok" ? "text-green-600" :
                        s.status === "failed" ? "text-red-600" :
                        s.status === "started" ? "text-blue-600" :
                        "text-gray-500"
                      }>
                        {s.status === "ok" ? "✓" : s.status === "failed" ? "✗" : s.status === "started" ? "…" : "○"}
                      </span>
                      <span className="capitalize">{s.step.replace(/_/g, " ")}</span>
                      {s.error && <span className="text-red-600 text-xs ml-2">({s.error})</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Final result */}
              {rotateDone && (
                <div className={`p-4 rounded border ${rotateDone.ok ? "bg-green-50 border-green-300 text-green-800" : "bg-red-50 border-red-300 text-red-800"}`}>
                  {rotateDone.ok ? (
                    <>
                      <div className="font-semibold">Rotation complete</div>
                      <div className="text-xs mt-1">
                        rotation_id: {rotateDone.rotation_id}
                        {rotateDone.version_hash && ` · hash: ${rotateDone.version_hash}`}
                      </div>
                      <div className="text-sm mt-2">
                        volcano-engine is now using the new key. Other consumers are unaffected.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold">
                        {rotateDone.rollback === "completed"
                          ? "Rotation failed — rolled back successfully"
                          : rotateDone.manual_recovery_required
                          ? "Rotation failed — manual recovery required"
                          : "Rotation failed"}
                      </div>
                      {rotateDone.error && <div className="text-xs mt-1">error: {rotateDone.error}</div>}
                      {rotateDone.rollback === "completed" && (
                        <div className="text-sm mt-2">
                          Vault has been restored to the previous version. Safe to retry.
                        </div>
                      )}
                      {rotateDone.manual_recovery_required && (
                        <div className="text-sm mt-2">
                          See the runbook at{" "}
                          <code className="bg-red-100 px-1 rounded">docs/runbooks/secrets-rotation-runbook.md</code>{" "}
                          for manual Vault rollback steps.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t flex gap-2 justify-end">
              <button
                className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
                onClick={closeRotate}
                disabled={rotateBusy}
              >
                {rotateDone ? "Close" : "Cancel"}
              </button>
              {!rotateDone && !rotateBusy && (
                <button
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                  onClick={runRotation}
                  disabled={!rotateUnderstood || !rotateValue.trim() || rotateBusy}
                >
                  Rotate
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

